import React, { useState } from "react";
import { Plus, CheckCircle2, Info, X, History } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { EquipmentModal } from "../components/EquipmentModal";
import { EquipmentTypeahead } from "../components/EquipmentTypeahead";
import { DisciplineBadge } from "../components/Badges";
import { useAuth } from "../context/AuthContext";
import { fmtDate, genKey } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Temperature", "Electronics", "Hydraulics", "Mass", "Flow"];

const blankItem = () => ({
  _k: genKey(),
  description: "", manufacturer: "", part_number: "", serial_number: "", issue_number: "",
  discipline: "Dimensional", quantity: 1, notes: "",
  matched_equipment_id: null, matched_meta: null,
});

export default function RequestQuote() {
  const { user } = useAuth();
  const customerId = user?.customer_id;
  const [items, setItems] = useState([blankItem()]);
  const [notes, setNotes] = useState("");
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(null);

  const setItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, blankItem()]);
  const removeItem = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const applyMatch = (i, m) => {
    setItem(i, {
      description: m.description || "",
      manufacturer: m.manufacturer || "",
      part_number: m.part_number || "",
      serial_number: m.serial_number || "",
      issue_number: m.issue_number || "",
      discipline: m.discipline || "Dimensional",
      matched_equipment_id: m.id,
      matched_meta: { last: m.last_calibration_date, due: m.calibration_due_date, history: m.calibration_history_count },
    });
    toast.success(`Loaded existing record · ${m.calibration_history_count || 0} past calibration(s)`);
  };

  const submit = async () => {
    const filled = items.filter((it) => it.description);
    if (filled.length === 0) return toast.error("Add at least one item");
    setBusy(true);
    try {
      const payload = {
        customer_notes: notes,
        items: filled.map((it) => ({
          description: it.description, manufacturer: it.manufacturer,
          part_number: it.part_number, serial_number: it.serial_number,
          issue_number: it.issue_number, discipline: it.discipline,
          quantity: parseInt(it.quantity) || 1, notes: it.notes,
          equipment_id: it.matched_equipment_id || null,
        })),
      };
      const r = await api.post("/rfqs", payload);
      setDone(r.data);
      setItems([blankItem()]);
      setNotes("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <PageHead crumb="My Account" title="Request a Quote"
        sub="Search your registered equipment to add it to a quote — or register a new item if it's not there yet." />
      <div className="notice" style={{ marginBottom: 18 }}><Info size={16} /><span>Prices shown are <b>estimates only</b> and subject to confirmation once equipment is received and assessed.</span></div>

      <div className="grid" style={{ gridTemplateColumns: "1.5fr .8fr" }}>
        <div className="card pad">
          <div className="sec-h"><h3>Equipment for quote</h3><span className="tag-soft">{items.filter((i) => i.description).length} / {items.length} item{items.length !== 1 ? "s" : ""}</span></div>

          {items.map((it, i) => (
            <div className="line-item" key={it._k}>
              <div className="lh">
                <div className="n">
                  <span className="li-chip">{i + 1}</span>{it.description || "New item"}
                  {it.matched_equipment_id && (
                    <span className="badge b-green" style={{ marginLeft: 10 }} data-testid={`rfq-matched-${i}`}>
                      <span className="bd" /> Existing — last cal {fmtDate(it.matched_meta?.last)} · {it.matched_meta?.history || 0} event(s)
                      <button className="icon-btn" style={{ marginLeft: 6 }} title="View history" onClick={() => setHistoryOpen(it.matched_equipment_id)}>
                        <History size={13} />
                      </button>
                    </span>
                  )}
                </div>
                {items.length > 1 && <button className="btn sm ghost" onClick={() => removeItem(i)} data-testid={`rfq-remove-${i}`}><X size={13} /></button>}
              </div>
              <div className="form-grid three">
                <div className="fld">
                  <label>Serial number</label>
                  <EquipmentTypeahead customerId={customerId} field="serial_number"
                    value={it.serial_number} placeholder="Type to search your equipment…"
                    onChange={(v) => setItem(i, { serial_number: v, matched_equipment_id: null })}
                    onPick={(m) => applyMatch(i, m)}
                    onCreateNew={() => toast.message("OK — fill in the rest of the fields and we'll register this as a new item.")}
                    testid={`rfq-serial-${i}`} />
                </div>
                <div className="fld">
                  <label>Part number</label>
                  <EquipmentTypeahead customerId={customerId} field="part_number"
                    value={it.part_number} placeholder="Type to search your equipment…"
                    onChange={(v) => setItem(i, { part_number: v, matched_equipment_id: null })}
                    onPick={(m) => applyMatch(i, m)}
                    onCreateNew={() => toast.message("OK — fill in the rest of the fields and we'll register this as a new item.")}
                    testid={`rfq-part-${i}`} />
                </div>
                <div className="fld">
                  <label>Issue number</label>
                  <EquipmentTypeahead customerId={customerId} field="issue_number"
                    value={it.issue_number} placeholder="Type to search your equipment…"
                    onChange={(v) => setItem(i, { issue_number: v, matched_equipment_id: null })}
                    onPick={(m) => applyMatch(i, m)}
                    onCreateNew={() => toast.message("OK — fill in the rest of the fields and we'll register this as a new item.")}
                    testid={`rfq-issue-${i}`} />
                </div>
                <div className="fld"><label>Description <span className="req">*</span></label><input className="input" value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="e.g. Vernier Caliper 0–150mm" data-testid={`rfq-desc-${i}`} /></div>
                <div className="fld"><label>Manufacturer</label><input className="input" value={it.manufacturer} onChange={(e) => setItem(i, { manufacturer: e.target.value })} /></div>
                <div className="fld"><label>Discipline</label><select className="input" value={it.discipline} onChange={(e) => setItem(i, { discipline: e.target.value })}>{DISCIPLINES.map((d) => <option key={d}>{d}</option>)}</select></div>
                <div className="fld"><label>Quantity</label><input className="input mono" type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} /></div>
                <div className="fld" style={{ gridColumn: "span 2" }}><label>Notes</label><input className="input" value={it.notes} onChange={(e) => setItem(i, { notes: e.target.value })} placeholder="Anything we should know (e.g. due urgently, known fault)" /></div>
              </div>
            </div>
          ))}

          <button className="btn ghost" style={{ width: "100%", justifyContent: "center", borderStyle: "dashed" }} onClick={addItem} data-testid="rfq-add-item"><Plus size={15} /> Add another item</button>
        </div>

        <div>
          <div className="card pad">
            <div className="sec-h"><h3>Submit</h3></div>
            <div className="fld" style={{ marginBottom: 12 }}><label>Notes for PTE</label><textarea className="input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0" }}>
              <span style={{ fontWeight: 700 }}>Items</span>
              <span className="mono" style={{ fontWeight: 800, fontSize: 16 }}>{items.filter((i) => i.description).length}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0 12px" }}>
              <span className="t-mut" style={{ fontSize: 12.5 }}>Linked to existing records</span>
              <span className="mono t-mut" style={{ fontSize: 12.5 }}>{items.filter((i) => i.matched_equipment_id).length}</span>
            </div>
            <button className="btn gold" style={{ width: "100%", justifyContent: "center", marginTop: 6 }} onClick={submit} disabled={busy} data-testid="rfq-submit"><CheckCircle2 size={15} /> {busy ? "Submitting…" : "Submit request"}</button>
            <p style={{ fontSize: 11.5, color: "var(--text-3)", textAlign: "center", marginTop: 10 }}>Estimate only · final pricing confirmed on assessment</p>
          </div>
          <div className="notice" style={{ marginTop: 16, fontSize: 12 }}>
            <Info size={14} />
            <span>Tip: start typing a <b>Serial</b>, <b>Part</b> or <b>Issue</b> number — your existing records will pop up so we can link the full history to this quote.</span>
          </div>
        </div>
      </div>

      {done && (
        <Modal open onClose={() => setDone(null)} icon={CheckCircle2} title="Quote request submitted" subtitle={`Reference ${done.reference}`}
          footer={<button className="btn gold" onClick={() => setDone(null)}>Done</button>}>
          <p style={{ fontSize: 13.5, color: "var(--text-2)", marginBottom: 14 }}>Thanks — your request for <b style={{ color: "var(--text)" }}>{done.item_count} item(s)</b> has been sent to PTE. Reception has been notified and will confirm pricing once the equipment is received and assessed.</p>
          <div className="notice"><Info size={16} /><span>Prices shown are estimates only and subject to confirmation once equipment is received and assessed.</span></div>
        </Modal>
      )}

      {historyOpen && <EquipmentModal id={historyOpen} canEdit={false} onClose={() => setHistoryOpen(null)} onChanged={() => {}} />}
    </>
  );
}
